import { Inject, Injectable } from '@angular/core';
import { TranslateDefaultParser, TranslateService, USE_DEFAULT_LANG } from '@ngx-translate/core';
import { CfTranslations, TranslationConfig, SupportedLanguage, CfTranslation } from './translation.types';
import { cf_window } from '../config';

@Injectable()
export class TranslationService {
  private i18n: CfTranslations = {};

  constructor(
    private translateService: TranslateService,
    private parser: TranslateDefaultParser,
    @Inject(USE_DEFAULT_LANG) private useDefaultLang: boolean = true
  ) {}

  /**
   * Gets the default cplace language
   */
  public static getDefaultLanguage(): string {
    const configLanguage = (window as cf_window)?._cf_fe20_config_?.language;
    if (configLanguage && Object.values<string>(SupportedLanguage).includes(configLanguage)) {
      return configLanguage;
    }
    return SupportedLanguage.EN;
  }

  /**
   * Stores translation keys and values
   * @param translationConfig
   */
  public addTranslation(translationConfig: TranslationConfig): void {
    const supportedLanguages: SupportedLanguage[] = Object.keys(translationConfig.translations) as SupportedLanguage[];
    supportedLanguages.forEach((lang: SupportedLanguage) => {
      const translations = translationConfig.translations[lang].default
        ? translationConfig.translations[lang].default
        : translationConfig.translations[lang];
      this.i18n[lang] = this.deepTranslationMerge(translations, this.i18n[lang]);
    });
  }

  /**
   * Gets instant (synchronous) translation
   * @param key Translation key
   * @param interpolateParams Translation interpolation
   */
  public getInstantTranslation(key: string, interpolateParams?: Record<string, unknown>): string {
    return this.instant(key, interpolateParams);
  }

  /**
   * Gets currently used language
   */
  public getCurrentLanguage(): string {
    return this.translateService.currentLang || this.translateService.defaultLang;
  }

  /**
   * Processes the translation and returns a value
   * @param translations Provided translations
   * @param key Translation key
   * @param interpolateParams Translation interpolation
   * @private
   */
  private getParsedResult(translations: CfTranslations, key: string, interpolateParams?: unknown): string {
    let res: string | undefined;

    if (translations) {
      res = this.parser.interpolate(this.parser.getValue(translations, key), interpolateParams);
    }

    if (
      typeof res === 'undefined' &&
      this.translateService.defaultLang != null &&
      this.translateService.defaultLang !== this.translateService.currentLang &&
      this.useDefaultLang
    ) {
      res = this.parser.interpolate(this.parser.getValue(this.i18n[this.translateService.defaultLang], key), interpolateParams);
    }

    return typeof res !== 'undefined' ? res : key;
  }

  /**
   * Instantly (synchronously) translates
   * @param key Translation key
   * @param interpolateParams Translation interpolation
   * @private
   */
  private instant(key: string, interpolateParams?: unknown): string {
    if (!key || !key.length) {
      throw new Error(`Parameter "key" required`);
    }
    return this.getParsedResult(this.i18n[this.getCurrentLanguage()] as CfTranslations, key, interpolateParams);
  }

  private deepTranslationMerge(source: CfTranslations | CfTranslation, target: CfTranslations | CfTranslation) {
    Object.keys(source).forEach((key) => {
      if (!target) {
        target = {};
      }
      if (!target[key]) {
        target[key] = source[key];
        return;
      }
      if (source[key] && typeof source[key] === 'object') {
        this.deepTranslationMerge(source[key] as CfTranslations | CfTranslation, target[key] as CfTranslations | CfTranslation);
      }
    });
    return target;
  }
}
